import { Box, Stack } from "@mui/material";
import { AdvertisementWidget } from "core/components/AdvertisementWidget";
import { PageContainer } from "core/components/PageContainer";
import { QuickLinksWidget } from "core/components/QuickLinksWidget/QuickLinksWidget";
import { useIsFeatureFlagEnabled } from "core/components/useFeatureFlags";
import { useBrandName } from "core/hooks/useBrandName";
import { useBreakpoints } from "core/hooks/useBreakpoints";
import { usePageLogging } from "core/hooks/usePageLogging";
import { DMSWidget } from "features/DMS/components/DMSWidget";
import { EventsWidgetV2 } from "features/NewsEvents/components/EventWidget/EventsWidgetV2";
import { NewsWidgetV2 } from "features/NewsEvents/components/NewsWidget/NewsWidgetV2";
import { SuggestionBoxWidget } from "features/SuggestionBox/components/SuggestionBoxWidget";
import { WeatherWidget } from "features/Weather/components";
import Head from "next/head";
import { useTranslation } from "react-i18next";
import { ReviewsWidget } from "../ReviewsWidget";
import { ShopMapWidget } from "./ShopMapWidget";

function DesktopLayout() {
  const shopMapFeatureFlagEnabled = useIsFeatureFlagEnabled(
    "interactiveMapWidgetActive"
  );
  const isSociReviewsEnabled = useIsFeatureFlagEnabled("sociReviewsActive");
  return (
    <Box
      sx={{
        display: "grid",
        columnGap: 5,
        rowGap: 2,
        gridAutoRows: "min-content",
        gridTemplateAreas: `
          "weather ads   ads   ads"
          "stack   news  news  news" 
          "dms     dms   dms   dms"
  `,
        gridTemplateColumns: "1fr 1fr 1fr 1fr",
      }}
    >
      <WeatherWidget />
      <Box gridArea="news" display="flex" flexDirection="row" gap={2}>
        <NewsWidgetV2 containerProps={{ stackProps: { flex: 2 } }} />
        <EventsWidgetV2
          containerProps={{ stackProps: { flex: 1 } }}
          hideIfEmpty
        />
      </Box>
      <AdvertisementWidget />
      <Stack gap={2} sx={{ gridArea: "stack" }}>
        {shopMapFeatureFlagEnabled && <ShopMapWidget />}
        <SuggestionBoxWidget />
        <QuickLinksWidget />
      </Stack>
      <Stack gap={2} sx={{ gridArea: "dms" }}>
        {isSociReviewsEnabled && <ReviewsWidget />}
        <DMSWidget />
      </Stack>
    </Box>
  );
}

function TabletLayout() {
  const shopMapFeatureFlagEnabled = useIsFeatureFlagEnabled(
    "interactiveMapWidgetActive"
  );
  const isSociReviewsEnabled = useIsFeatureFlagEnabled("sociReviewsActive");
  return (
    <Stack gap={2}>
      <WeatherWidget />
      <NewsWidgetV2 />
      <EventsWidgetV2 hideIfEmpty />
      <AdvertisementWidget />
      <Stack
        sx={{
          "& > *": {
            flex: { xs: "1 1 300px", lg: "unset" },
          },
        }}
        direction={{ xs: "row", lg: "column" }}
        flexWrap={{ xs: "wrap", lg: "nowrap" }}
        gap={2}
      >
        {shopMapFeatureFlagEnabled && <ShopMapWidget />}
        <SuggestionBoxWidget />
        <QuickLinksWidget />
      </Stack>
      {isSociReviewsEnabled && <ReviewsWidget />}
      <DMSWidget />
    </Stack>
  );
}

function MobileLayout() {
  const shopMapFeatureFlagEnabled = useIsFeatureFlagEnabled(
    "interactiveMapWidgetActive"
  );
  const isSociReviewsEnabled = useIsFeatureFlagEnabled("sociReviewsActive");
  return (
    <Stack gap={2}>
      <WeatherWidget />
      <AdvertisementWidget />
      <NewsWidgetV2 />
      <EventsWidgetV2 hideIfEmpty />
      {shopMapFeatureFlagEnabled && <ShopMapWidget />}
      <SuggestionBoxWidget />
      <QuickLinksWidget />
      {isSociReviewsEnabled && <ReviewsWidget />}
      <DMSWidget />
    </Stack>
  );
}

function Wrapper({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation("Fixhub", {
    keyPrefix: "Next:Core:RegionLandingPage",
  });
  const brandName = useBrandName();
  return (
    <>
      <Head>
        <title>{t("headTitle", { brandName })}</title>
        <meta name="description" content="Pitstop landing page" />
      </Head>

      <PageContainer data-testid="landing-page" component="main" maxWidth="xl">
        {children}
      </PageContainer>
    </>
  );
}

export function DefaultLandingPage() {}

export function LandingPage() {
  usePageLogging({
    itemType: "Page",
    itemId: "home",
    componentName: "LandingPage",
  });
  const { isMobile, isTablet } = useBreakpoints();
  if (isMobile) {
    return (
      <Wrapper>
        <MobileLayout />
      </Wrapper>
    );
  }
  if (isTablet) {
    return (
      <Wrapper>
        <TabletLayout />
      </Wrapper>
    );
  }
  return (
    <Wrapper>
      <DesktopLayout />
    </Wrapper>
  );
}
